import { DashboardRegistryRow } from "./interface/dashboardRegistry";

// Mock of the future backend `_dashboard` registry seed. `key`/`internal_view` values are copied
// verbatim from docs/dashboard-registry-seed-checklist.md's original 33 top-level + 23 promoted
// POI rows (§6/"Row-by-row checklist"), NOT derived from MENU_ELEMENTS - that constant
// deliberately excludes `archived` and all POIs (see searchableDashboards.ts), which is exactly
// what this registry must include. 23 top-level + 23 POI rows remain below - see the "10 rows
// deliberately absent" note below for why the top-level count dropped from 33. Swap this file's
// export for a real GET /dashboard endpoint once Sayli's seed lands - consumers only go through
// getDashboardRegistryRows() in dashboardsApi.ts.
//
// Two corrections vs. the checklist's literal values, both cross-referenced in the checklist's own
// §8 "bugs found during audit":
// - `ariya_experience_monitor`'s path is the live route `/ariya/monitor`, not `/ariya/monitor.html`
//   (that's a <Redirect> target in App.tsx, never a real location.pathname). Still open, and worse
//   than "just a mock discrepancy": the real GET /dashboards response below (2026-08-12) shows the
//   actual backend seed row still has the buggy `.html` path, unfixed on that side - so once this
//   file's export is swapped for live data, ariya's exact-match will fail against the real row
//   unless the backend seed itself gets corrected first. Flagging for whoever owns that seed.
// - `poi_use_ownership_self_service`'s path is `/use_ownership/self_service` (the real route,
//   App.tsx), not menuItems.ts's `use_ownership/self_service` (missing its leading slash - a
//   frontend bug in the POI list itself, independent of this registry). The same real response
//   confirms the backend seed already has this one right (`/use_ownership/self_service`, leading
//   slash present) - unlike ariya, no outstanding backend-side fix needed here.
//
// A 2026-08-12 real GET /dashboards response (46 rows) showed no row at all for 10 dashboards,
// believed at the time (per product confirmation) to be the real final seed state - they were
// moved out of this registry into Bucket-B residual rules (accessControlResidual.ts). A newer
// real response since then (2026-08-14, 41 rows) showed that belief was wrong for 7 of the 10:
// abt_data_query, correspondence_mapping, pricing_project, verbatim_analysis, visit_to_lead,
// serious_buyer, and the configurator dashboard (ids 34-40) all now have real rows, so - per
// product decision - they're back below. Two corrections vs. their old Bucket-B entries:
// - The configurator dashboard's real `key` is `configurator_g5` (underscore), not the old
//   hyphenated `configurator-g5` - that's a change to the backend's own `key` field only. Every
//   existing hardcoded reference elsewhere in this codebase (App.tsx, accessControl.ts,
//   menuItems.ts, pathTracking.ts, PowerBiTemplate.tsx) keys off the literal URL PATH
//   (`/beta/configurator-g5`), which the real response still returns unchanged - so none of those
//   needed updating, only this row's own `key` field does.
// - The real response's name for this row, "Configurator (G5, Jp, Nibu)", suggests it may be a
//   consolidation of what used to be tracked as 3 separate configurator dashboards - but
//   `new_beta_car_configurator` and `configurator_nibu` are each still genuinely absent from every
//   real response seen so far (unlike configurator_g5), so they're deliberately left out of this
//   batch and still gated via their own Bucket-B rules.
// Only `clv` and the archived top-level `product_performance` dashboard are still confirmed
// absent - see accessControlResidual.ts's own updated comment for those two.
//
// `midas_overview` (bare `/midas`, below) stays flagged as mock-only/unconfirmed rather than
// removed outright, unlike the 10 above - that same real response also shows no bare `/midas`
// row, but this one hasn't had an explicit product decision recorded yet the way the 10 above
// just did. Flagging again for whoever finalizes the seed migration.
export const DASHBOARD_REGISTRY: DashboardRegistryRow[] = [
  // 23 top-level dashboards (originally 33 - see file comment above)
  { key: "seo_report", internal_view: "seo_report", path: "/seo_dashboard", is_active: true },
  { key: "brand_performance", internal_view: "brand_performance", path: "/brand_performance", is_active: true },
  { key: "voc_website", internal_view: "voc", path: "/voc/customer_journey_voc", is_active: true },
  { key: "online_ce_performance", internal_view: "online_ce_performance", path: "/digital_performance", is_active: true },
  // Seeded after the original 33+23 audit this file's header comment describes - confirmed
  // directly against the backend's own real seed migration (cedar-backend's oce-digital-trend
  // branch: key/name/description/keywords correction migration), not guessed. internal_view reuses
  // "online_ce_performance" verbatim (same as the poi_cpo_digital_performance row below) rather
  // than a new permission - this is just another page in that same family.
  { key: "oce_digital_trends", internal_view: "online_ce_performance", path: "/oce_digital_trend", is_active: true },
  { key: "ecommerce", internal_view: "ecommerce", path: "/ecommerce", is_active: true },
  {
    key: "product_marketing_optimization",
    internal_view: "product_marketing_optimization",
    path: "/product_marketing_optimization",
    is_active: true,
  },
  { key: "use_ownership", internal_view: "use_ownership", path: "/use_ownership/overview", is_active: true },
  { key: "ccs_vital_signs", internal_view: "ccs_vital_signs", path: "/ccs_vital_signs/domo", is_active: true },
  { key: "global_ms_sales", internal_view: "global_ms_sales", path: "/global_ms_sales/ignitems", is_active: true },
  { key: "executive_summary", internal_view: "executive_summary", path: "/executive_summary/dis", is_active: true },
  { key: "trim_mix_report", internal_view: "trim_mix_report", path: "/trim_mix_report", is_active: true },
  { key: "configurator_nibu", internal_view: "car_configurator_nibu", path: "/beta/configurator-nibu", is_active: true },
  {
    key: "lead_management_report",
    internal_view: "lead_management_report",
    path: "/lead_management_report/introduction",
    is_active: true,
  },
  { key: "mobility_report", internal_view: "mobility_report", path: "/mobility_report", is_active: true },
  {
    key: "digital_customer_experience",
    internal_view: "digital_customer_experience",
    path: "/digital_user_experience",
    is_active: true,
  },
  {
    key: "online_to_physical_insights",
    internal_view: "online_to_physical_insights",
    path: "/online_to_physical",
    is_active: true,
  },
  { key: "ariya_experience_monitor", internal_view: "ariya_experience_monitor", path: "/ariya/monitor", is_active: true },
  { key: "cxo_experiment_library", internal_view: "cxo_experiment_library", path: "/beta/cxo_experiment", is_active: true },
  { key: "mtm_metrics_that_matter", internal_view: "mtm_metrics_that_matter", path: "/mtm_metrics", is_active: true },
  { key: "midas", internal_view: "midas", path: "/midas/fmi_run_rate", is_active: true },
  { key: "midas_overview", internal_view: "midas", path: "/midas", is_active: true }, // mock-only, see file comment above
  { key: "dmeo", internal_view: "dmeo", path: "/dmeo", is_active: true },
  { key: "search_interest", internal_view: "search_interest", path: "/search_interest", is_active: true },
  { key: "ccs_cockpit", internal_view: "ccs_vital_signs", path: "/ccs_vital_signs/ccs_cockpit", is_active: true },

  // 7 rows moved back from Bucket-B (see file comment above) - confirmed real ids 34-40 in the
  // 2026-08-14 GET /dashboards response. Of the 6 Analytics Tools rows, only 4 (ids 34-37) use the
  // new "mi_analytics" internal_view - per a follow-up product/backend confirmation, those 4 are
  // splitting off of the shared "streamlit" view into their own; visit_to_lead/serious_buyer
  // (ids 38-39) stay on "streamlit". PAGE_VIEW_MAPPING (accessControl.ts) is NOT updated to match -
  // it's a frozen copy of the legacy system's own behavior, which still treats all 6 as one group.
  { key: "abt_data_query", internal_view: "mi_analytics", path: "/streamlit/abt_data_query", is_active: true },
  { key: "correspondence_mapping", internal_view: "mi_analytics", path: "/streamlit/correspondence_mapping", is_active: true },
  { key: "pricing_project", internal_view: "mi_analytics", path: "/streamlit/pricing_project", is_active: true },
  { key: "verbatim_analysis", internal_view: "mi_analytics", path: "/streamlit/verbatim_analysis", is_active: true },
  { key: "visit_to_lead", internal_view: "streamlit", path: "/streamlit/visit_to_lead_tool", is_active: true },
  { key: "serious_buyer", internal_view: "streamlit", path: "/streamlit/serious_buyer", is_active: true },
  // `key` is the real backend value (underscore) - see file comment above for why every other
  // hardcoded reference in this codebase keeps the unchanged hyphenated PATH instead.
  { key: "configurator_g5", internal_view: "car_configurator_g5", path: "/beta/configurator-g5", is_active: true },

  // 23 promoted points-of-interest rows (docs/dashboard-registry-seed-checklist.md §6)
  {
    key: "poi_seo_dashboard_search_interest_report",
    internal_view: "search_interest",
    path: "/seo_dashboard/search_interest_report",
    is_active: true,
  },
  { key: "poi_seo_dashboard_geo_reporting", internal_view: "geo_reporting", path: "/seo_dashboard/geo_reporting", is_active: true },
  { key: "poi_voc_website", internal_view: "voc", path: "/voc/website", is_active: true },
  {
    key: "poi_ccs_vital_signs_app_ratings",
    internal_view: "ccs_vital_signs",
    path: "/ccs_vital_signs/app_ratings",
    is_active: true,
  },
  { key: "poi_voc_quick_voc_sales", internal_view: "voc", path: "/voc/quick_voc_sales", is_active: true },
  { key: "poi_voc_quick_voc_after_sales", internal_view: "voc", path: "/voc/quick_voc_after_sales", is_active: true },
  { key: "poi_voc_cec_voc", internal_view: "voc", path: "/voc/cec_voc", is_active: true },
  {
    key: "poi_cpo_digital_performance",
    internal_view: "online_ce_performance",
    path: "/cpo_digital_performance",
    is_active: true,
  },
  {
    key: "poi_journey_excellence_market_performance_summary",
    internal_view: "journey_excellence",
    path: "/journey_excellence/market_performance_summary",
    is_active: true,
  },
  { key: "poi_cpo_operational", internal_view: "cpo_operational", path: "/cpo_operational", is_active: true },
  {
    key: "poi_use_ownership_online_account_and_engagement",
    internal_view: "use_ownership",
    path: "/use_ownership/online_account_and_engagement",
    is_active: true,
  },
  {
    key: "poi_use_ownership_service_lead",
    internal_view: "use_ownership",
    path: "/use_ownership/service_lead",
    is_active: true,
  },
  {
    key: "poi_use_ownership_self_service",
    internal_view: "use_ownership",
    path: "/use_ownership/self_service", // corrected, see file comment above
    is_active: true,
  },
  { key: "poi_ccs_vital_signs", internal_view: "ccs_vital_signs", path: "/ccs_vital_signs", is_active: true },
  {
    key: "poi_ccs_vital_signs_ccs_monetization",
    internal_view: "ccs_vital_signs",
    path: "/ccs_vital_signs/ccs_monetization",
    is_active: true,
  },
  { key: "poi_global_ms_sales_tier_one", internal_view: "reignite_user", path: "/global_ms_sales/tier_one", is_active: true },
  {
    key: "poi_global_ms_sales_tier_two",
    internal_view: "reignite_manager",
    path: "/global_ms_sales/tier_two",
    is_active: true,
  },
  {
    key: "poi_global_ms_sales_tier_three",
    internal_view: "reignite_user",
    path: "/global_ms_sales/tier_three",
    is_active: true,
  },
  // The "link your Google account" page (formerly poi_global_ms_sales_pdf_page /
  // /global_ms_sales/pdf_page) moved to PUBLIC_AUTHENTICATED_PATHS
  // (accessControlResidual.ts) instead of staying registry-gated here - it's linked from several
  // unrelated dashboards (SeoFilters.tsx, TrimMixReport.tsx), not just Global M&S Sales, so
  // gating it behind the "global_ms_sales" view specifically denied access to users reaching it
  // from those other dashboards. Now at /faq/link_google_account, generic and open to any
  // authenticated user, same as /faq itself.
  {
    key: "poi_global_ms_sales_handbook_pdf_page",
    internal_view: "global_ms_sales",
    path: "/global_ms_sales/handbook_pdf_page",
    is_active: true,
  },
  { key: "poi_purchase_funnel_report", internal_view: "purchase_funnel", path: "/purchase_funnel_report", is_active: true },
  {
    key: "poi_executive_summary_product_performance",
    internal_view: "executive_summary",
    path: "/executive_summary/product_performance",
    is_active: true,
  },
  {
    key: "poi_executive_summary_product_performance_deep_dive",
    internal_view: "executive_summary",
    path: "/executive_summary/product_performance_deep_dive",
    is_active: true,
  },
];
